import React, { useState, useEffect, useMemo } from 'react'
import injectStyles from 'react-jss'
import cn from 'classnames'

import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import InputAdornment from '@material-ui/core/InputAdornment'

import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'

import styles from './styles.js'
import {
  calculateTotalCostDependingOfType,
  filterTableKeysByCampaignType,
  generateTotalRow,
  getComparator,
  insertBeforeTotalCost,
  isValueValid,
  roundTo2,
  stableSort,
} from './utils'
import { Select } from '@material-ui/core'
import { MenuItem } from 'material-ui'
import DeleteIcon from '../../Assets/Images/icons/deleteIcon'
import { theme } from 'Assets/Styles/theme'
import StyledIconButton from '../Form/StyledIconButton'
import { handleConfirmationDialog } from '../../Containers/City/Page/Ambasadors/EmailPipelineView/utils'
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog'
import { CITY_METRICS_HEADERS } from './options'

const CityMetricTable = ({
  classes,
  currencySign = '$',
  citiesMetrics = [],
  campaignData,
  cities,
  citiesMetricsValues,
  setCitiesMetricsValues,
  uploadedCityMetrics,
  disabled = false,
  isUnitedStatesH2HorD2D,
  isUnitedStatesCoreDM,
}) => {
  const [totalRow, setTotalRow] = useState([])
  const [confirmDialogData, setConfirmDialogData] = useState({ isOpen: false })
  const isSelfServe = useMemo(
    () =>
      campaignData &&
      campaignData.campaign &&
      campaignData.campaign.isSelfServe,
    [campaignData],
  )

  const formatedCityList = cities.map(city => ({
    value: city.id,
    label: city.name,
  }))
  const headers = filterTableKeysByCampaignType(
    campaignData.type,
    CITY_METRICS_HEADERS,
  )

  if (isUnitedStatesH2HorD2D || isUnitedStatesCoreDM) {
    insertBeforeTotalCost(headers, [
      {
        key: 'tax',
        label: 'Sales Tax %',
        type: 'tax',
      },
      {
        key: 'taxAmount',
        label: 'Sales Tax Amount',
        type: 'taxAmount',
      },
      {
        key: 'subtotal',
        label: 'Subtotal',
        type: 'subtotal',
      },
    ])
  }

  const handleCellChange = (value, row, key) => {
    const newCityMetrics = citiesMetricsValues.map(city => {
      return city.id === row.id
        ? {
            ...city,
            [key]: Number(value) == 0 ? null : Number(value),
          }
        : city
    })
    const cityMetricsWithTotal = calculateTotalCostDependingOfType(
      newCityMetrics,
      campaignData.type,
    )

    const generatedTotalRow = generateTotalRow(
      cityMetricsWithTotal,
      campaignData.type,
      isUnitedStatesH2HorD2D,
      isUnitedStatesCoreDM,
    )

    setTotalRow(generatedTotalRow)
    setCitiesMetricsValues(cityMetricsWithTotal)
  }

  const renderTableCell = (row, key, type) => {
    const uniqKey = `${row.id}-${key}`
    switch (type) {
      case 'currencySignTotal':
        return (
          <Typography key={uniqKey}>
            {currencySign}
            {roundTo2(row[key])}
          </Typography>
        )
      case 'tax': {
        return <Typography key={uniqKey}>{row[key]}%</Typography>
      }
      case 'taxAmount': {
        return (
          <Typography key={uniqKey}>
            {currencySign}
            {roundTo2(row[key])}
          </Typography>
        )
      }
      case 'subtotal': {
        return (
          <Typography key={uniqKey}>
            {currencySign}
            {roundTo2(row[key])}
          </Typography>
        )
      }
      case 'dropdownCity':
        return (
          <Select
            key={uniqKey}
            className={classes.select}
            value={row[key]}
            onChange={event => handleCellChange(event.target.value, row, key)}
            disableUnderline={true}
            disabled={disabled}
          >
            <MenuItem value={null}>Select City</MenuItem>

            {formatedCityList.map(city => {
              return <MenuItem value={city.value}>{city.label}</MenuItem>
            })}
          </Select>
        )
      case 'currencySign':
        return (
          <OutlinedInput
            key={uniqKey}
            id={uniqKey}
            type="number"
            value={row[key]}
            sx={{
              '& .notchedOutline': {
                border: 'none',
              },
            }}
            onChange={event => {
              const { value } = event.target

              return isValueValid(value)
                ? handleCellChange(value, row, key)
                : null
            }}
            startAdornment={
              <InputAdornment position="center">{currencySign}</InputAdornment>
            }
            classes={{
              input: classes.outlinedInput,
              root: classes.outlinedInputRoot,
            }}
            disabled={disabled}
          />
        )
      case 'number':
        return (
          <OutlinedInput
            key={uniqKey}
            id={uniqKey}
            value={row[key]}
            type="number"
            onChange={event => {
              const { value } = event.target

              return isValueValid(value)
                ? handleCellChange(value, row, key)
                : null
            }}
            classes={{
              input: classes.outlinedNumberInput,
              root: classes.outlinedInputRoot,
            }}
            disabled={disabled}
          />
        )
      case 'delete': {
        return !disabled ? (
          <div
            key={row.id}
            className={classes.deleteButtonContainer}
            onClick={() => deleteCity(row.id)}
          >
            <div className={classes.buttonTitle}>
              <DeleteIcon color={theme.colors.primary} />
            </div>
          </div>
        ) : null
      }

      default:
        return <Typography key={uniqKey}>{row[key]}</Typography>
    }
  }

  const TotalRow = ({ totalRow }) => {
    return (
      <TableRow hover={false} tabIndex={-1}>
        {totalRow.map((item, index) => {
          const showCurrencySign = ![
            'totalFlyers',
            'totalMissions',
            'tax',
          ].includes(item.key)
          return (
            <TableCell
              align={index === 0 ? 'left' : 'center'}
              className={
                index !== 0
                  ? classes.tableHeaderCell
                  : classes.firstTableHeaderCell
              }
            >
              {item.label ? (
                <Typography className={classes.tableHeadersLabel}>
                  {item.label}
                </Typography>
              ) : item.value ? (
                <Typography className={classes.tableHeadersLabel}>
                  {showCurrencySign && currencySign}
                  {item.value}
                </Typography>
              ) : (
                <Typography className={classes.tableHeadersLabel}>
                  {'-'}
                </Typography>
              )}
            </TableCell>
          )
        })}
      </TableRow>
    )
  }

  useEffect(() => {
    const cityMetricsWithTotal = calculateTotalCostDependingOfType(
      citiesMetrics,
      campaignData.type,
    )
    const generatedTotalRow = generateTotalRow(
      cityMetricsWithTotal,
      campaignData.type,
      isUnitedStatesH2HorD2D,
      isUnitedStatesCoreDM,
    )
    setTotalRow(generatedTotalRow)
    setCitiesMetricsValues(cityMetricsWithTotal)
  }, [citiesMetrics, cities])

  useEffect(() => {
    if (uploadedCityMetrics) {
      const cityMetricsWithTotal = calculateTotalCostDependingOfType(
        uploadedCityMetrics,
        campaignData.type,
      )
      const generatedTotalRow = generateTotalRow(
        cityMetricsWithTotal,
        campaignData.type,
        isUnitedStatesH2HorD2D,
        isUnitedStatesCoreDM,
      )
      setTotalRow(generatedTotalRow)
      setCitiesMetricsValues(cityMetricsWithTotal)
    }
  }, [uploadedCityMetrics, cities])

  const handleSelfServeAddCity = () => {
    handleConfirmationDialog({
      title: 'Restricted!',
      contentText: `You cannot manually add city metrics for self-serve campaigns. If you need to do this, please contact tech.`,
      setConfirmDialogData,
      rightButtonLable: 'OK',
    })
  }

  const addCity = () => {
    const newCityLength = citiesMetricsValues.filter(item =>
      String(item.id).includes('newCity'),
    ).length
    const newCityMetrics = {
      id: `newCity-${newCityLength}`,
      cityId: null,
      totalFlyers: null,
      totalMissions: null,
      distributionCost: null,
      travelCost: null,
      permitCost: null,
      printingCost: null,
      bonusBudget: null,
      postingCost: null,
      dataCost: null,
      outfitCost: null,
      freightCost: null,
      state: null,
      postalCode: null,
    }
    const newMetrics = [...citiesMetricsValues, newCityMetrics]
    setCitiesMetricsValues(newMetrics)
  }

  const deleteCity = id => {
    const newMetrics = citiesMetricsValues.filter(item => item.id !== id)
    setCitiesMetricsValues(newMetrics)

    const cityMetricsWithTotal = calculateTotalCostDependingOfType(
      newMetrics,
      campaignData.type,
    )
    const generatedTotalRow = generateTotalRow(
      cityMetricsWithTotal,
      campaignData.type,
      isUnitedStatesH2HorD2D,
      isUnitedStatesCoreDM,
    )
    setTotalRow(generatedTotalRow)
  }

  return (
    <Box className={classes.wrapper}>
      {confirmDialogData.isOpen && <ConfirmDialog {...confirmDialogData} />}
      <Box className={classes.table}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size="medium">
            <TableHead className={classes.tableHead}>
              <TableRow className={classes.tableRow}>
                {headers.map((headCell, index) => (
                  <TableCell
                    key={headCell.key}
                    align={index === 0 ? 'left' : 'center'}
                    className={
                      index !== 0
                        ? classes.tableHeaderCell
                        : classes.firstTableHeaderCell
                    }
                  >
                    <Typography className={classes.tableHeadersLabel}>
                      {headCell.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(citiesMetricsValues, getComparator('abs', 'id')).map(
                row => {
                  return (
                    <TableRow hover={false} tabIndex={-1} key={row.id}>
                      {headers.map(({ key, type }, index) => {
                        return index === headers.length - 1 ? (
                          <div className={classes.rowContainer}>
                            <TableCell
                              align={'center'}
                              className={cn(type ? classes.padding10 : {})}
                            >
                              {renderTableCell(row, key, type, cities)}
                            </TableCell>
                          </div>
                        ) : (
                          <TableCell
                            align={index === 0 ? 'left' : 'center'}
                            className={cn(type ? classes.padding10 : {})}
                          >
                            {renderTableCell(row, key, type, cities)}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                },
              )}
              <TotalRow totalRow={totalRow} />
            </TableBody>
            {!disabled ? (
              <div
                className={classes.buttonContainer}
                onClick={isSelfServe ? handleSelfServeAddCity : addCity}
              >
                <StyledIconButton
                  className={classes.addButton}
                  label="Add City"
                >
                  +
                </StyledIconButton>
                <span className={classes.buttonLabel}>Add City</span>
              </div>
            ) : null}
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}

export default injectStyles(styles)(CityMetricTable)
